/**
 * 多语言。复用官网 public/i18n/<lang>.json 与同一个 localStorage 键（tp_lang），
 * 玩家在官网选的语言进到游戏里继续生效。
 *
 * 与官网那份脚本的差别：这里是 ES 模块、支持 await 首屏加载完再渲染，
 * 且路径用绝对 /i18n/ —— /play/ 下的相对路径会指错。
 */

const SUPPORTED = ['en', 'zh-Hans', 'zh-Hant', 'ja', 'ko', 'fr', 'de', 'es', 'pt-BR', 'it'];

/** 各语言的自称。语言选单一律用母语显示，不随当前界面语言变。 */
export const LANG_NAMES = {
    'en': 'English',
    'zh-Hans': '简体中文',
    'zh-Hant': '繁體中文',
    'ja': '日本語',
    'ko': '한국어',
    'fr': 'Français',
    'de': 'Deutsch',
    'es': 'Español',
    'pt-BR': 'Português',
    'it': 'Italiano',
};
const DEFAULT = 'en';
const STORAGE_KEY = 'tp_lang';
const RTL = ['ar', 'he', 'fa', 'ur'];

let dict = {};
let current = DEFAULT;

/**
 * 按浏览器语言挑一个受支持的语言。
 * @param {string | null} stored
 */
function pickLang(stored) {
    if (stored && SUPPORTED.includes(stored)) return stored;

    const list = navigator.languages || [navigator.language || DEFAULT];
    for (const tag of list) {
        if (SUPPORTED.includes(tag)) return tag;            // 精确命中，如 pt-BR

        const base = tag.split('-')[0].toLowerCase();
        if (base === 'zh') {                                 // 中文按字形分繁简
            const t = tag.toLowerCase();
            const hant = t.includes('hant') || t.includes('tw') ||
                t.includes('hk') || t.includes('mo');
            return hant ? 'zh-Hant' : 'zh-Hans';
        }
        const hit = SUPPORTED.find(s => s.split('-')[0].toLowerCase() === base);
        if (hit) return hit;
    }
    return DEFAULT;
}

/**
 * 取一条文案。
 * @param {string} path 点分路径，如 'play.success.title'
 * @param {string} [fallback] 缺词时用的英文原文
 * @returns {string}
 */
export function t(path, fallback = '') {
    const v = path.split('.').reduce((o, k) => (o && o[k] != null ? o[k] : null), dict);
    return v == null ? fallback : String(v);
}

/** 当前语言。 */
export function lang() {
    return current;
}

/** 受支持语言列表。 */
export function supported() {
    return SUPPORTED.slice();
}

/**
 * 加载语言包并把 [data-i18n] 元素填上。
 * @param {string} [target] 指定语言，缺省则自动挑
 */
/**
 * 拉一个语言包。
 *
 * 先带条件请求核对（etag → 多半是 304，很便宜），失败再吃缓存：
 * 只用 `force-cache` 的话，改过的文案对老访客永远不存在 —— 缓存里有旧版就直接用、
 * 连服务器都不问，新加的词条会静默退回英文（2026-07-31 踩过）。
 * 反过来只用 `no-cache`，离线开局就彻底没文案了，所以两级都要。
 *
 * @param {string} code
 * @returns {Promise<object | null>}
 */
async function loadDict(code) {
    for (const mode of ['no-cache', 'force-cache']) {
        try {
            const res = await fetch(`/i18n/${code}.json`, { cache: mode });
            if (res.ok) return await res.json();
        } catch { /* 换下一档 */ }
    }
    return null;
}

export async function initI18n(target) {
    let stored = null;
    try {
        stored = localStorage.getItem(STORAGE_KEY);
    } catch { /* 忽略 */ }

    current = target || pickLang(stored);

    let loaded = await loadDict(current);
    // 语言包拉不到就退回英文兜底（t() 的 fallback 参数都是英文原文）
    if (!loaded && current !== DEFAULT) {
        current = DEFAULT;
        loaded = await loadDict(DEFAULT);
    }
    if (loaded) dict = loaded;

    applyDom();
    return current;
}

/** 把当前语言包应用到带 data-i18n 的元素上。 */
export function applyDom(root = document) {
    document.documentElement.lang = current;
    document.documentElement.dir =
        RTL.includes(current.split('-')[0]) ? 'rtl' : 'ltr';

    root.querySelectorAll('[data-i18n]').forEach(el => {
        const v = t(el.getAttribute('data-i18n'), '');
        if (!v) return;
        if (el.tagName === 'META') el.setAttribute('content', v);
        else el.textContent = v;
    });
    root.querySelectorAll('[data-i18n-alt]').forEach(el => {
        const v = t(el.getAttribute('data-i18n-alt'), '');
        if (v) el.setAttribute('alt', v);
    });
    root.querySelectorAll('[data-i18n-aria]').forEach(el => {
        const v = t(el.getAttribute('data-i18n-aria'), '');
        if (v) el.setAttribute('aria-label', v);
    });
}

/**
 * 切换语言并记住。
 * @param {string} next
 */
export async function setLang(next) {
    if (!SUPPORTED.includes(next)) return current;
    try {
        localStorage.setItem(STORAGE_KEY, next);
    } catch { /* 忽略 */ }
    return initI18n(next);
}
